import React from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ExternalLink, ChevronDown, Shield, Rocket, Users, Award, TrendingUp, Lock } from "lucide-react";
import { Link } from "react-router-dom";

/* ===== Images ===== */
import chematico from "../../images/chematico.jpeg";
import evigway from "../../images/evigway.jpeg";
import fulectronix from "../../images/FULECTRONIX_TECHNOLOGY.jpg";
import greeninv from "../../images/GREEN_INNOVOTECH.jpg";
import jyoma from "../../images/JYOMA_SOLUTIONS.jpg";
import renao from "../../images/renao.jpg";
import rosch from "../../images/rosch-crete.jpg";
import ironfist from "../../images/ironfist.png";
/* Hero Background */
import heroBg from "../../images/defencevisit.png";

/* Defence Startups */
const defenceStartups = [
  { img: jyoma, name: "Jyoma Solutions", link: "https://jyomasolutions.com/", tag: "Surveillance", description: "Advanced surveillance systems for national security" },
  { img: evigway, name: "Evigway Technologies", link: "http://www.evigway.com/", tag: "EV Tech", description: "Electric vehicle solutions for defense mobility" },
  { img: ironfist, name: "Ironfist Arms", link: "https://www.ironfistarms.org/", tag: "Arms & Munitions", description: "Advanced weaponry and ammunition solutions" },
  { img: chematico, name: "Chematico Technologies", link: "http://www.chematico.com/", tag: "Materials", description: "Specialized materials for defense applications" },
  { img: rosch, name: "Roschcrete Technologies", link: "http://roschcrete.com/", tag: "Specialized Concrete", description: "High-performance concrete for military infrastructure" },
  { img: fulectronix, name: "Fulectronix Technologies", link: "http://www.fulectronix.in/", tag: "Robotics", description: "Robotic solutions for defense operations" },
  { img: greeninv, name: "Green Innovotech", link: "http://www.greeninnovotech.com/", tag: "Clean Tech", description: "Sustainable technology for defense sector" },
  { img: renao, name: "Renao Robotics", link: "https://www.nanokriti.com/", tag: "Tactical Robotics", description: "Tactical robotic systems and solutions" },
];


export default function DefenceStartup() {
  const { scrollYProgress } = useScroll();
  const yContent = useTransform(scrollYProgress, [0, 0.2], [0, -40]);
  const opacity = useTransform(scrollYProgress, [0, 0.15], [1, 0]);

  const handleScrollToContent = () => {
    window.scrollTo({ top: window.innerHeight * 0.85, behavior: "smooth" });
  };

  return (
    <div className="bg-slate-50 min-h-screen font-sans text-slate-900 overflow-x-hidden">

      {/* ================= DEFENCE HERO ================= */}
      <section className="relative h-[90vh] min-h-[600px] w-full overflow-hidden flex items-end">

        {/* Background Image with Professional Ken Burns Effect */}
        <div className="absolute inset-0 z-0">
          <motion.img
            src={heroBg}
            alt="Defence Innovation"
            className="w-full h-full object-cover object-center"
            initial={{ scale: 1.2, opacity: 0 }}
            animate={{ scale: 1, opacity: 0.6 }}
            transition={{
              duration: 2.5,
              ease: "easeOut",
            }}
          />
          {/* Technical Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/60 to-transparent" />
          <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-[0.03]" />
          
          {/* Animated Grid Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: 'linear-gradient(rgba(59, 130, 246, 0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(59, 130, 246, 0.5) 1px, transparent 1px)',
              backgroundSize: '60px 60px'
            }} />
          </div>
        </div>

        {/* Content Overlay */}
        <motion.div 
          style={{ y: yContent, opacity }}
          className="relative z-10 w-full pb-20 md:pb-24 px-4"
        >
          <div className="max-w-6xl mx-auto text-center">
      

            {/* Main Heading */}
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.8 }}
              className="text-5xl md:text-6xl lg:text-7xl font-black text-white mb-6 tracking-tighter leading-[0.95]"
            >
              Innovation in <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-blue-500 to-indigo-500">
                Defence & Security
              </span>
            </motion.h1>

            {/* Subtext */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="text-slate-300 text-lg md:text-xl max-w-3xl mx-auto leading-relaxed mb-10 font-light"
            >
              Empowering startups to strengthen national security through cutting-edge 
              surveillance, robotics, advanced materials, and mission-critical systems.
            </motion.p>


          </div>
        </motion.div>

        {/* Scroll Indicator */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1, duration: 1 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 hidden md:flex flex-col items-center gap-2"
        >
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            className="flex flex-col items-center gap-3 cursor-pointer group"
            onClick={handleScrollToContent}
          >
            <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-slate-400 group-hover:text-cyan-400 transition-colors duration-300">
              Explore
            </span>
            <div className="relative flex items-center justify-center p-3 rounded-full bg-white/10 backdrop-blur-xl border border-white/20 group-hover:border-cyan-400/50 group-hover:bg-white/15 transition-all duration-500 shadow-2xl">
              <ChevronDown size={22} className="text-slate-300 group-hover:text-cyan-400 transition-colors duration-300" strokeWidth={2.5} />
            </div>
          </motion.div>
        </motion.div>

        {/* Subtle Bottom Border */}
        <div className="absolute bottom-0 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent" />
      </section>



      {/* ================= SECTION HEADER ================= */}
      <section className="max-w-7xl mx-auto px-6 pt-12 pb-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center max-w-3xl mx-auto"
        >
          <h2 className="text-3xl md:text-4xl font-black text-slate-900 mb-4 tracking-tight">
            Our Defence Ecosystem
          </h2>
          <p className="text-slate-600 text-lg leading-relaxed">
            Meet the innovative startups driving the future of national security and defense technology
          </p>
        </motion.div>
      </section>

      {/* ================= ENHANCED STARTUP GRID ================= */}
      <section className="max-w-7xl mx-auto px-6 pb-24">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {defenceStartups.map((startup, index) => (
            <motion.div
              key={startup.name}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ delay: index * 0.05, duration: 0.5 }}
              className="group bg-white rounded-2xl overflow-hidden border border-slate-200 shadow-md hover:shadow-2xl hover:border-blue-400 transition-all duration-500"
            >
              {/* Image Container with Overlay */}
              <div className="relative h-48 w-full bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-8 overflow-hidden">
                {/* Animated Background Pattern */}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 to-cyan-50/50" />
                </div>
                
                <img
                  src={startup.img}
                  alt={`${startup.name} logo`}
                  className="relative z-10 max-h-full max-w-full object-contain transition-transform duration-500 group-hover:scale-110"
                />
                
                {/* Tag Badge */}
                <div className="absolute top-4 right-4 z-20">
                  <span className="inline-block px-3 py-1.5 rounded-full text-xs font-bold text-blue-700 bg-white/90 backdrop-blur-sm border border-blue-200 shadow-sm">
                    {startup.tag}
                  </span>
                </div>
              </div>

              {/* Content */}
              <div className="p-6 flex flex-col">
                <h3 className="text-xl font-bold text-slate-900 mb-2 group-hover:text-blue-600 transition-colors duration-300">
                  {startup.name}
                </h3>
                
                <p className="text-sm text-slate-600 leading-relaxed mb-6 flex-grow">
                  {startup.description}
                </p>

                <a
                  href={startup.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-600 text-white font-bold text-sm hover:from-blue-700 hover:to-cyan-700 transition-all duration-300 shadow-md hover:shadow-lg hover:-translate-y-0.5 group/btn"
                >
                  Visit Website 
                  <ExternalLink size={16} className="group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5 transition-transform duration-300" />
                </a>
              </div>
            </motion.div>
          ))}
        </div>
      </section>



      {/* ================= CTA SECTION ================= */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 border border-blue-200 mb-6">
              <Rocket size={16} className="text-blue-600" />
              <span className="text-sm font-semibold text-blue-700 tracking-wide">
                Join Our Ecosystem
              </span>
            </div>

            <h2 className="text-4xl md:text-5xl font-black text-slate-900 mb-6 tracking-tight">
              Building for Defence?
            </h2>
            
            <p className="text-slate-600 text-lg md:text-xl mb-10 max-w-2xl mx-auto leading-relaxed">
              Partner with our Centre of Excellence to access specialized labs,
              funding opportunities, and structured government pathways.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link
                to="https://forms.gle/Fz2pn47EqTscZK4T8"
                className="inline-flex items-center gap-2.5 bg-gradient-to-r from-blue-600 to-cyan-600 text-white px-8 py-4 rounded-xl font-bold text-base hover:from-blue-700 hover:to-cyan-700 transition-all shadow-lg hover:shadow-xl hover:-translate-y-1 duration-300"
              >
                Get Started
                <ExternalLink size={20} />
              </Link>
              
              <button className="inline-flex items-center gap-2.5 bg-white text-slate-700 px-8 py-4 rounded-xl font-bold text-base border-2 border-slate-300 hover:border-blue-500 hover:text-blue-600 transition-all duration-300">
                Learn More
                <ChevronDown size={20} className="rotate-[-90deg]" />
              </button>
            </div>
          </motion.div>
        </div>
      </section>

    </div>
  );
}